import { usePermisos } from '@/hooks/usePermisos';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { Navigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';

/**
 * Componente para proteger rutas basado en permisos
 */
export const ProtectedRoute = ({ 
  children, 
  requiredPermission = null, 
  requiredRole = null, 
  fallbackPath = '/unauthorized' 
}) => {
  const { 
    verificarPermiso, 
    isSuperAdmin, 
    usuarioEmpresa, 
    loading, 
    empresasDisponibles,
    isAdmin: isAdminContext,
    isMecanico: isMecanicoContext,
    isCliente: isClienteContext
  } = useSimpleEmpresa();
  const { 
    hasManagementRole,
    hasOperationalRole 
  } = usePermisos();
  
  // Usar directamente del contexto para evitar problemas de timing
  const isAdmin = () => isAdminContext;
  const isMecanico = () => isMecanicoContext;
  const isCliente = () => isClienteContext;

  // COMENTADO: No mostrar loading - permitir renderizado reactivo
  // Los datos se cargan de forma reactiva y los componentes se actualizan automáticamente
  /*
  // Mostrar loading mientras se cargan los datos de empresa
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Cargando permisos...</p>
        </div>
      </div>
    );
  }

  // Esperar a que se hayan cargado las empresas disponibles para determinar el SuperAdmin status
  if (empresasDisponibles.length === 0 && isSuperAdmin === false && !usuarioEmpresa) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Verificando permisos...</p>
        </div>
      </div>
    );
  }
  */

  // Si es SuperAdmin, siempre permitir acceso
  if (isSuperAdmin) {
    return (
      <Layout>
        {children}
      </Layout>
    );
  }

  // Solo verificar permisos si los datos están completamente cargados
  // Mientras se cargan, permitir acceso (comportamiento reactivo)
  if (!loading && empresasDisponibles.length > 0) {
    // Verificar rol específico si se requiere
    if (requiredRole) {
      const hasRole = checkRole(requiredRole, {
        isAdmin,
        isMecanico,
        isCliente,
        hasManagementRole,
        hasOperationalRole
      });

      if (!hasRole) {
        return <Navigate to={fallbackPath} replace />;
      }
    }

    // Verificar permiso específico si se requiere
    if (requiredPermission) {
      const [recurso, accion] = requiredPermission.split('.');
      const hasPermission = verificarPermiso(recurso, accion);

      if (!hasPermission) {
        return <Navigate to={fallbackPath} replace />;
      }
    }

    // Si no hay restricciones específicas, verificar que al menos esté autenticado
    if (!usuarioEmpresa) {
      return <Navigate to="/taller" replace />;
    }
  }

  // Log de éxito

  // Envolver el contenido con Layout para incluir sidebar
  return (
    <Layout>
      {children}
    </Layout>
  );
};

// Helper para verificar roles
const checkRole = (requiredRole, roleCheckers) => {
  switch (requiredRole) {
    case 'admin':
      return roleCheckers.isAdmin();
    case 'mecanico':
      return roleCheckers.isMecanico();
    case 'cliente':
      return roleCheckers.isCliente();
    case 'management':
      return roleCheckers.hasManagementRole();
    case 'operational':
      return roleCheckers.hasOperationalRole();
    default:
      return false;
  }
};

export default ProtectedRoute;